import LiveModeParticipantsIcon from "@common/header/LiveModeParticipantsIcon"
import CallKeepManagerM from "../../../engine/live/room/callKeep/CallKeepManagerM"
import LiveWebMobileBanner from "@common/liveMode/LiveWebMobileBanner"
import TutorLiveScreen from "@common/liveMode/liveV3/TutorLiveScreen"
import NotificationSubscriptionHandler from "@common/notifications/NotificationSubscriptionHandler"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { updateLastConnected } from "@data/redux/actions/authTokens"
import { setIsLiveModeEnabled } from "@data/redux/actions/liveMode"
import { hasRole } from "@data/utility/ability"
import { supportsNoShowRecovery } from "@data/utility/liveSessionType"
import React, { useRef } from "react"
import { Platform } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import { useConnectionState } from "@livekit/components-react"
import ActivityWrapperScreen from "../../../student/activity/execute/ActivityWrapperScreen"
import StudentLiveModeFooter from "../../header/StudentLiveModeFooter"
import LKVideoCallPortal from "../../livekit/LKVideoCallPortal"
import { useInterval } from "../../utils/useInterval"
import {
    TutorNoShowProvider,
    RecoveryModal,
    CreditModal,
    InitialDialog,
    MiniModeWaitingBar
} from "@common/hooks/livekit/tutorNoShow"
import { TutorOverrunDialog } from "@common/hooks/livekit/tutorOverrun"
import { liveRoomSignalsFor } from "@data/redux/reducers/liveMode"
import { useTabManagement } from "../../../engine/live/room/useTabManagement"
import { KeepTabModal } from "@common/liveMode/KeepTabModal"

const LiveContainerV3 = (props) => {
    const liveSession = props.currentLiveSession
    const connectionState = useConnectionState()
    const isConnected = connectionState === "connected"
    const tutorParticipants = liveSession?.users?.filter((user) => user?.is_tutor) ?? []
    const presentTutors = tutorParticipants?.filter((user) => props.participants?.includes(user?.id)) ?? []
    const isStudentInSession = hasRole(props.user, "student") && isConnected
    const isGroupSession = supportsNoShowRecovery(liveSession?.live_room)
    const skipCleanupRef = useRef(false)
    const navigation = useNavigation()

    const isSoundMode =
        hasRole(props.user, "student") &&
        props.soundModeIds?.includes(props.user?.id)

    // Only enable tutor no-show feature for students in group sessions
    const shouldEnableTutorNoShow = hasRole(props.user, "student") && isGroupSession

    // Tab management for web - shows modal when multiple tabs detected
    const {
        showKeepTabModal,
        handleKeepThisTab,
        hasOtherTabs,
        isWeb
    } = useTabManagement(props.liveSessionId)

    useInterval(() => {
        if (props.user?.email) {
            props.updateLastConnected(props?.user)
        }
    }, 5 * 60 * 1000)

    const content = (
        <>
            {Platform.OS !== "web" && <NotificationSubscriptionHandler />}

            {/* Tab Management Modal - web only */}
            {isWeb && (
                <KeepTabModal
                    isVisible={showKeepTabModal}
                    onKeepThisTab={handleKeepThisTab}
                />
            )}

            {/* Tutor No-Show UI Components - Only for no-show-recoverable group sessions (see supportsNoShowRecovery) */}
            {isStudentInSession && isGroupSession && (
                <>
                    <RecoveryModal />
                    <CreditModal />
                    <InitialDialog user={props.user} />
                </>
            )}

            {/* Le tuteur qui déborde sur sa séance suivante : un pop-up, une fois par occurrence */}
            {hasRole(props.user, "tutor") && (
                <TutorOverrunDialog tutorOverrun={props.liveRoomSignals?.tutor_overrun} />
            )}

            {props.getLiveSessionRequest && !liveSession ? (
                <ActivityIndicator
                    color={theme.colors.primary}
                    animating={true}
                    size={"large"}
                />
            ) : hasRole(props.user, "student") ? (
                <>
                    {/* Rappel web mobile quand la vidéo est réduite : dans ce cas
                        le portal ne couvre plus l'écran, c'est ici le haut du live.
                        Cet écran sert aussi le natif, d'où la sortie explicite. */}
                    {Platform.OS === "web" && props.miniMode && (
                        <LiveWebMobileBanner />
                    )}
                    {/* Waiting bar for miniMode (only shows when waiting for tutor) */}
                    {isGroupSession && <MiniModeWaitingBar />}
                    <ActivityWrapperScreen />
                    <StudentLiveModeFooter
                        height={70}
                        miniMode={false}
                        setMiniMode={() => {}}
                        setFocusedMode={() => {}}
                        nav={props.nav}
                        route={props.route}
                        backgroundColor={theme.colors.white}
                        isSoundMode={isSoundMode}
                    />
                </>
            ) : (
                <TutorLiveScreen otherTabPresent={hasOtherTabs} />
            )}
            <CallKeepManagerM />
            {hasRole(props.user, "student") && (
                <LKVideoCallPortal
                    otherTabPresent={hasOtherTabs}
                    roomLink={liveSession?.conference_link}
                    suspended={props.cameraOn}
                    users={liveSession?.users}
                    renderTopLeftComponent={() => <LiveModeParticipantsIcon />}
                />
            )}
        </>
    )

    // Le provider est monté INCONDITIONNELLEMENT, et c'est `enabled` qui décide
    // s'il agit.
    //
    // Il était auparavant monté seulement pour les séances de groupe, via un
    // ternaire autour de `content`. Or `shouldEnableTutorNoShow` dépend de
    // `liveSession?.live_room`, qui n'arrive qu'après le premier rendu : à la
    // bascule, React voyait un type d'élément différent à cette position, donc
    // détruisait tout le sous-arbre live et le reconstruisait. Le démontage de
    // `LKVideoCallPortal` appelait alors `room.disconnect(true)` — asynchrone,
    // il atterrissait après le remontage et tuait la room dont héritait le
    // nouveau portail. L'élève restait bloqué sur « Connexion… ».
    return (
        <TutorNoShowProvider
            enabled={shouldEnableTutorNoShow}
            liveSession={liveSession}
            presentTutors={presentTutors}
            isStudentInSession={isStudentInSession}
            apiRequest={props.apiRequest}
            apiIdle={props.apiIdle}
            room={null}
            navigation={navigation}
            setIsLiveModeEnabled={props.setIsLiveModeEnabled}
            skipCleanupRef={skipCleanupRef}
        >
            {content}
        </TutorNoShowProvider>
    )
}

const mapStateToProps = (state) => {
    return {
        roomData: state.liveKitRoom?.roomData,
        getLiveSessionRequest: state.api.getLiveSessionV2?.request,
        getCallRequest: state.api.getCallV2?.request,
        user: state.data.currentUser?.object,
        successes: state.data.staticData?.successes,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
        soundModeIds: state.liveMode?.soundModeIds,
        participants: state.liveMode?.liveParticipants,
        miniMode: state.liveMode?.miniMode,
        liveRoomSignals: liveRoomSignalsFor(state, state.liveMode?.liveSessionId),
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    updateLastConnected,
    setIsLiveModeEnabled,
}

export default connect(mapStateToProps, mapDispatchToProps)(LiveContainerV3)
